import type { CreateBlockItemRequest, ParsedItem } from '../../api/trainingBlockApi'
import type { TrainingBlockItem, TrainingBlockItemExercise } from '../../types/api'

export const MAX_EXERCISES_PER_ITEM = 10

export interface ItemRow {
  note: string
  names: string[]
}

export function emptyItem(): ItemRow {
  return { note: '', names: [''] }
}

export function itemsFromApi(items: TrainingBlockItem[] | undefined | null): ItemRow[] {
  if (!items || items.length === 0) return [emptyItem()]
  return items.map(item => ({
    note: item.note ?? '',
    names: item.exercises?.length
      ? item.exercises.map(ex => ex.name ?? '')
      : [''],
  }))
}

export function itemsFromParsed(items: ParsedItem[] | undefined | null): ItemRow[] {
  if (!items || items.length === 0) return [emptyItem()]
  return items.map(item => ({
    note: item.note ?? '',
    names: item.exercises?.length
      ? item.exercises.map(ex => ex.name ?? '')
      : [''],
  }))
}

export function itemsToApi(items: ItemRow[]): CreateBlockItemRequest[] {
  return items.map(item => ({
    note: item.note.trim() || null,
    exercises: item.names
      .map(name => name.trim())
      .filter(Boolean)
      .map(name => ({ name })),
  })).filter(item => item.exercises.length > 0)
}

export function countExercisesInItems(items: { exercises?: { name?: string }[] }[] | undefined | null): number {
  if (!items) return 0
  return items.reduce((sum, item) => sum + (item.exercises?.length ?? 0), 0)
}

export function formatItemNames(item: { exercises?: { name?: string }[] } | ItemRow): string {
  if ('names' in item) {
    return item.names.filter(Boolean).join(' · ')
  }
  return (item.exercises ?? []).map(ex => ex.name ?? '').filter(Boolean).join(' · ')
}

export function sortExercisesInItem(
  exercises: TrainingBlockItemExercise[] | undefined | null,
): TrainingBlockItemExercise[] {
  if (!exercises?.length) return []
  return [...exercises].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

export function flattenExerciseNamesFromItems(
  items: { sortOrder?: number; exercises?: TrainingBlockItemExercise[] }[] | undefined | null,
): string[] {
  if (!items?.length) return []
  const sortedItems = [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  const names: string[] = []
  for (const item of sortedItems) {
    for (const ex of sortExercisesInItem(item.exercises)) {
      const name = ex.name?.trim()
      if (name) names.push(name)
    }
  }
  return names
}
